/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core/styles';
import {useAuthenticatedUser} from '@ion/applications/src/pages/admin/users/useAuthenticatedUser';
import Avatar from '@ion/dls/src/components/avatar/Avatar';
import NotificationButton from '@ion/dls/src/components/buttons/NotificationButton';
import Render from '@ion/dls/src/render/Render';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {HeaderTypography} from '@ion/dls/src/typography/HeaderTypography';
import {getPageTitle} from '@/routes/RouteDefinitions';
import {toggleUserProfileView} from '@/store/actions/nav';
import {
  currentPageSelector
} from '@/store/selectors/navigation';
import UserProfileMobile from '../components/profile/UserProfileMobile';
import JobStatusToggle from './JobStatusToggle';
import useJobNotificationsPanel from './useJobNotificationsPanel';
import styles from './MobileHeader.module.scss';

export interface HeaderProps {
  onNavigation?: Function;
  isLoggingOut?: boolean;
}

const useStyles = makeStyles((theme) => ({
  profileTitle : {
    width      : '100%',
    textAlign  : 'center',
    marginTop  : 6,
    marginLeft : 40
  },
  ionLink : {
    ...dlsAlt('components', 'ionToggle', 'ionBeta')
  },
  betaLink : {
    border          : `4px solid ${dlsColor('caBlue')}`,
    backgroundColor : dlsColor('caBlue'),
    borderRadius    : '20px',
    ...dlsAlt('components', 'ionpreviewText', 'preview')
  },
  ionBeta : {
    marginLeft : '-88px',
    display    : 'flex',
    alignItems : 'center'
  }
}));

const iconsTooltip = makeStyles((theme) => ({
  arrow : {
    color : dlsColor('grey1')
  },
  tooltip : {
    ...dlsAlt('components', 'iconTooltip')
  }
}));

/**
 * The Mobile Header component provides the application Header for the ctc-admin Application
 * when view in Mobile Mode.
 */
export const MobileHeader = () => {
  const classes     = useStyles();
  const dispatch    = useDispatch();
  const {username}  = useAuthenticatedUser();
  const currentPage = useSelector(currentPageSelector);
  const {
    show,
    unreadCount,
    toggle: toggleNotifications
  }                 = useJobNotificationsPanel();
  const [isUserProfile, setIsUserProfile] = useState(false);

  const handleClose = useCallback(function() {
    setIsUserProfile(false);
    dispatch(toggleUserProfileView(false));
  }, []);

  const onAvatarClick = useCallback(function() {
    setIsUserProfile(true);
    onUserProfile();
  }, []);

  const onUserProfile = useCallback(function() {
    dispatch(toggleUserProfileView(true));
  }, []);

  return (
    <div className={styles.flexContainer}
      style={{borderBottom: isUserProfile ? `1px solid ${dlsColor('grey5')}` : 'initial'}}>
      {
        currentPage && (
          <HeaderTypography variant='h2' className={classes.profileTitle}>
            {getPageTitle(currentPage)}
          </HeaderTypography>
        )
      }
      <Render.If condition={show}>
        <NotificationButton notificationCount={unreadCount}
          toggleNotificationPanel={toggleNotifications} />
      </Render.If>
      <JobStatusToggle />
      <div className={styles.rightContent}>
        <span className={styles.rightSubContent}>
          <Avatar className={styles.avatar}
            name={username}
            onClick={onAvatarClick} />
        </span>
      </div>
      <UserProfileMobile showProfile={isUserProfile} handleClose={handleClose} />
    </div>
  );
};
export default React.memo(MobileHeader);
